import { FormDialogMode, useNotify } from '@globaleris/ui.library'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { missingFieldsFormWithRequiredText } from '../../../helpers/validateFormHelper'
import { FormDialogDefinitionProps, useFormDialog } from '../../../hooks'
import {
  FormStateConfig,
  InputFieldConfig
} from '../genericCustomFormDialog/genericCustomFormDialog.model'

interface AnnexedParamCustomFormDialogProps {
  formStateConfig: FormStateConfig
  onClose: (formData: any) => void
  title: string
  subtitle?: string | React.ReactNode
}

export const AnnexedParamCustomFormDialog: React.FC<AnnexedParamCustomFormDialogProps> = ({
  formStateConfig,
  onClose,
  title,
  subtitle = ''
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'annexedParamCustomFormDialog'])
  const {
    openForm,
    updateFormData,
    updateFormDefinition,
    closeForm,
    formProps,
    CustomFormDialog,
    formHookProps,
    setFormWaiting
  } = useFormDialog()
  const { info, error } = useNotify()
  const { open, formMode, inputFields, inputData } = formStateConfig

  // #region Handlers Starts
  const onFormCloseHandler = async (formData: any) => {
    if (formData) {
      if (missingFieldsFormWithRequiredText(formDefinitionWithInputFields?.sections!, formData)) {
        error(
          t(
            'annexedParamCustomFormDialog:components.customAnnexedParamFormDialog.notifications.incompleteForm'
          )
        )
        return
      }
    }
    closeForm()
    onClose(formData)
  }
  // #endregion Handlers Ends

  // #region Form Definition Starts

  const formDefinitionWithInputFields = React.useMemo<FormDialogDefinitionProps>(() => {
    return {
      id: 'ANNEXED_PARAM_CUSTOM_FORM',
      sections: [
        {
          inputs: inputFields.map((inputField: InputFieldConfig) => ({
            name: inputField.name,
            label: inputField.label,
            type: inputField.type,
            required: inputField.required ?? false,
            selectOptions: inputField.selectOptions ?? undefined,
            maxLength: inputField.maxLength ?? undefined,
            display: inputField.display ?? true,
            defaultValue: inputField.defaultValue ?? undefined,
            inputSize: inputField.inputSize ?? undefined,
            readOnly: inputField.readOnly ?? false,
            isLoading: inputField.isLoading ?? false,
            loadingText: inputField.loadingText ?? undefined,
            clearable: inputField.clearable ?? false,
            regexp: inputField.regexp ?? undefined,
            helpText: inputField.helpText ?? undefined,
            errorText: inputField.errorText ?? undefined,
            suffix: inputField.suffix ?? undefined,
            interval: inputField.interval ?? undefined,
            placeholder: inputField.placeholder ?? undefined,
            apiCall: inputField.apiCall ?? undefined,
            apiCallDelay: inputField.apiCallDelay ?? undefined,
            apiCallMinQueryLength: inputField.apiCallMinQueryLength ?? undefined,
            onChange: (value, formState) => {
              const newFormState = { ...formState, [inputField.name]: value }
              updateFormData(newFormState)
              inputField.onChange && inputField.onChange(value, formState)
            },
            onClear: inputField.onClear ?? undefined,
            onFocus: inputField.onFocus ?? undefined,
            onClickAway: inputField.onClickAway ?? undefined,
            onBlur: inputField.onBlur ?? undefined
          }))
        }
      ]
    }
  }, [inputFields])

  // #endregion Form Definition Ends

  // #region Hooks Starts
  useEffect(() => {
    if (open && inputFields.length > 0) {
      if (!openForm) {
        return
      }
      openForm({
        definition: formDefinitionWithInputFields,
        open: true,
        title: title,
        subtitle: subtitle,
        mode: formMode,
        isWaiting: false,
        ...(formMode === FormDialogMode.EDIT && { inputData: { ...inputData } }),
        onClose: onFormCloseHandler
      })
    }
  }, [open, inputFields, formMode])

  useEffect(() => {
    updateFormDefinition(formDefinitionWithInputFields, inputData)
  }, [formDefinitionWithInputFields, inputData])

  // #endregion Hooks Ends

  return (
    <>
      <CustomFormDialog
        {...formProps}
        open={open}
        onClose={onFormCloseHandler}
        title={title}
        subtitle={subtitle}
        mode={formMode}
      />
    </>
  )
}
